package components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.ionspin.kotlin.bignum.decimal.BigDecimal
import com.ionspin.kotlin.bignum.decimal.DecimalMode
import com.ionspin.kotlin.bignum.decimal.RoundingMode
import com.ionspin.kotlin.bignum.decimal.toBigDecimal
import frontend.lib.classList
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*

class TWTextInputAttrsBuilder {
    var onChange: (value: String) -> Unit = {}
    var onFocusOut: () -> Unit = {}
    var onInputCallback: (value: String) -> Unit = {}
    var label: @Composable() () -> Unit = {}
    var type: InputType<*> = InputType.Text
    var isNumeric = false
    var isDisabled = false
    var isCondensed = false
    var isAutoFocus = false

    fun onChange(handler: (value: String) -> Unit) { onChange = handler }
    fun onInput(handler: (value: String) -> Unit) { onInputCallback = handler }
    fun onFocusOut(handler: () -> Unit) { onFocusOut = handler }
    fun label(l: @Composable() () -> Unit) { label = l }
    fun type(t: InputType<*>) { type = t }

    fun autoFocus() { isAutoFocus = true }

    // Hide password input with *
    fun password() { type = InputType.Password }

    // Allow only numeric input
    fun numeric() { isNumeric = true }

    fun onInputNumeric(value: String) {
        val regex = Regex("^[+-]?((\\d+(\\.\\d*)?)|(\\.\\d+))\$")

        if (value == "" || regex.matches(value)) onInputCallback(value)
    }

    fun onInputFinal(value: String) {
        if (isNumeric) return onInputNumeric(value)
        onInputCallback(value)
    }

    fun disabled() { isDisabled = true }
    fun condensed() { isCondensed = true }
}

@Composable
fun TTextInput(
    id: String,
    text: String,
    attrs: TWTextInputAttrsBuilder.() -> Unit = {}
) {
    val builder = TWTextInputAttrsBuilder()
    attrs.invoke(builder)

    Div() {
        Label(forId = id, attrs = {
            classes("block", "text-sm", "font-medium", "text-gray-700")
        }) {  builder.label() }

        Div(attrs = { classes("mt-1") }) {
            Input(type = builder.type) {
                id(id)
                classes("appearance-none", "block", "w-full", "px-3",
                    if (builder.isCondensed) "py-1" else "py-2",
                    "border", "border-gray-300", "rounded-md", "shadow-sm", "placeholder-gray-400", "focus:outline-none", "focus:ring-indigo-500", "focus:border-indigo-500", "sm:text-sm")
                value(text)
                name("text")
                onChange { builder.onChange(it.value.toString()) }
                onInput { builder.onInputFinal(it.value.toString()) }
                onFocusOut { builder.onFocusOut() }
                if (builder.isDisabled) disabled()
                if (builder.isAutoFocus) autoFocus()
            }
        }
    }
}

class TWDecimalInputAttrsBuilder {
    var onChangeCallback: (value: BigDecimal?) -> Unit = {}
    var onFocusOut: () -> Unit = {}
    var onInputCallback: (value: BigDecimal?) -> Unit = {}
    var label: @Composable() () -> Unit = {}
    var type: InputType<*> = InputType.Text
    var isDisabled = false
    var isCondensed = false
    var isAutoFocus = false
    var placeholder = ""
    var underInput = ""
    var precision: Long? = null

    fun onChange(handler: (value: BigDecimal?) -> Unit) { onChangeCallback = handler }
    fun onInput(handler: (value: BigDecimal?) -> Unit) { onInputCallback = handler }
    fun label(l: @Composable() () -> Unit) { label = l }

    fun getDecimal(value: String): BigDecimal? {
        val regex = Regex("^[+-]?((\\d+(\\.\\d*)?)|(\\.\\d+))\$")
        if (value == "" || !regex.matches(value)) return null
        val p = precision
        val bd = value.toBigDecimal()
        return if (p != null) bd.scale(p) else bd
    }

    fun onInput(value: String) {
        val decimal = getDecimal(value)
        if (decimal?.toPlainString() == value || value == "") onInputCallback(decimal)
    }

    fun onChange(value: String) {
        onChangeCallback(getDecimal(value))
    }

    fun disabled() { isDisabled = true }
    fun condensed() { isCondensed = true }

    fun underInput(str: String) { underInput = str }

    fun precision(p: Long) { precision = p }
}

@Composable
fun TDecimalInput(
    id: String,
    num: BigDecimal?,
    attrs: TWDecimalInputAttrsBuilder.() -> Unit = {}
) {
    val builder = TWDecimalInputAttrsBuilder()
    attrs.invoke(builder)
    var stringValue by mutableStateOf(num?.toPlainString() ?: "")

    fun checkPrecision(str: String): Boolean {
        println("${builder.precision}")
        if (builder.precision == null) return true
        else {
            builder.precision?.let {
                if (str == "") return true

                val parts = str.split(".")
                if (parts.size < 2) return true

                val second = parts.last()
                return second.length <= it
            } ?: return false
        }
    }

    Div() {
        Label(forId = id, attrs = {
            classes("block",
                if (builder.isCondensed) "text-xs" else "text-sm",
                "font-medium", "text-gray-700")
        }) {  builder.label() }

        Div(attrs = { classes("mt-1") }) {
            Input(type = builder.type) {
                id(id)
                classes("appearance-none", "block", "w-full", "px-3",
                    if (builder.isCondensed) "py-1" else "py-2",
                    "border", "border-gray-300", "rounded-md", "shadow-sm", "placeholder-gray-400", "focus:outline-none", "focus:ring-indigo-500", "focus:border-indigo-500", "sm:text-sm")
                value(stringValue)
                name("text")
                if (builder.isCondensed) placeholder(builder.placeholder)
                onChange {
                    val numString = it.value.toString()
                    if (builder.getDecimal(numString) == null) stringValue = ""
                    builder.onChange(numString)
                }
                onInput {
                    val regex = Regex("^[+-]?((\\d+(\\.\\d*)?)|(\\.\\d+))\$")
                    val str = it.value.toString()
                    if ((regex.matches(str) || str == "") && checkPrecision(str)) stringValue = str
                    builder.onInput(str)
                }
                onFocusOut { builder.onFocusOut() }
                if (builder.isDisabled) disabled()
                if (builder.isAutoFocus) autoFocus()
            }
            if (builder.underInput != "") Span(attrs = { classList("text-xs text-gray-500") }) {
                Text(builder.underInput)
            }
        }
    }
}
